/**
 * Supplier evaluation, supplier scorecard and supplier report routes (FSD 4.2.2, P45 PS5-U65).
 *
 * Literal `path:` strings (R3: the conformance deep-link parser cannot read a constant).
 * INT spreads `EVALUATION_ROUTES` into `PROCUREMENT_ROUTES` and adds the nav items
 * (`proc-supplier-evaluations`, `proc-document-expiry`, `proc-onboarding-cycle`,
 * `proc-supplier-concentration`; `proc-scorecards` and `proc-supplier-risk` already route).
 *
 * Each permission is the one the screen's main read declares on the server.
 */

import type { RouteRecordRaw } from 'vue-router'

import { EVALUATION_PERMISSIONS } from '@/modules/procurement/evaluation/api'

export const EVALUATION_ROUTES: RouteRecordRaw[] = [
  {
    path: '/procurement/supplier-evaluations',
    name: 'procurement-supplier-evaluations',
    component: () => import('@/modules/procurement/evaluation/EvaluationsListView.vue'),
    meta: {
      titleKey: 'procurement.u65.routeTitles.evaluations',
      permission: EVALUATION_PERMISSIONS.scorecardRead,
    },
  },
  {
    path: '/procurement/supplier-evaluations/:id(\\d+)',
    name: 'procurement-supplier-evaluation',
    component: () => import('@/modules/procurement/evaluation/EvaluationView.vue'),
    meta: {
      titleKey: 'procurement.u65.routeTitles.evaluation',
      permission: EVALUATION_PERMISSIONS.scorecardRead,
    },
  },
  {
    path: '/procurement/suppliers/:vendorId(\\d+)/scorecard',
    name: 'procurement-supplier-scorecard',
    component: () => import('@/modules/procurement/evaluation/SupplierScorecardView.vue'),
    meta: {
      titleKey: 'procurement.u65.routeTitles.supplierScorecard',
      permission: EVALUATION_PERMISSIONS.scorecardRead,
    },
  },
  {
    path: '/procurement/reports/supplier-document-expiry',
    name: 'procurement-supplier-document-expiry',
    component: () => import('@/modules/procurement/evaluation/reports/DocumentExpiryReportView.vue'),
    meta: {
      titleKey: 'procurement.u65.routeTitles.documentExpiry',
      permission: EVALUATION_PERMISSIONS.reportRead,
    },
  },
  {
    path: '/procurement/reports/supplier-onboarding-cycle',
    name: 'procurement-supplier-onboarding-cycle',
    component: () => import('@/modules/procurement/evaluation/reports/OnboardingCycleReportView.vue'),
    meta: {
      titleKey: 'procurement.u65.routeTitles.onboardingCycle',
      permission: EVALUATION_PERMISSIONS.reportRead,
    },
  },
  {
    path: '/procurement/reports/supplier-concentration',
    name: 'procurement-supplier-concentration',
    component: () =>
      import('@/modules/procurement/evaluation/reports/SupplierConcentrationReportView.vue'),
    meta: {
      titleKey: 'procurement.u65.routeTitles.concentration',
      permission: EVALUATION_PERMISSIONS.reportRead,
    },
  },
]

/** The same set as strings, for links inside the module. */
export const EVALUATION_PATHS = {
  evaluations: '/procurement/supplier-evaluations',
  evaluation: (id: number) => `/procurement/supplier-evaluations/${id}`,
  supplierScorecard: (vendorId: number) => `/procurement/suppliers/${vendorId}/scorecard`,
  documentExpiry: '/procurement/reports/supplier-document-expiry',
  onboardingCycle: '/procurement/reports/supplier-onboarding-cycle',
  concentration: '/procurement/reports/supplier-concentration',
  supplierRisk: '/procurement/reports/supplier-risk',
  scorecards: '/procurement/vendor-scorecards',
} as const
